/**
 * Support Case Detail (/admin/support/:caseId)
 *
 * FI-operator audience (fi_admin / fi_loan_officer / fi_compliance):
 *   GET    /api/v1/support/fi-cases/{caseId}
 *   GET    /api/v1/support/fi-cases/{caseId}/messages
 *   POST   /api/v1/support/fi-cases/{caseId}/messages
 *   (no PATCH, no escalate, no transfer)
 *
 * A session holding a platform-admin role sees the operator-console pointer
 * card and makes no request. GH #678 (operator ruling 2026-10-23) moved the
 * operator controls on this page (status and priority via PATCH
 * /api/v1/support/cases/{caseId}, escalate, transfer) and the operator read
 * and reply on /api/v1/support/cases/{caseId} to platform-admin,
 * platform.aaim.com /support/:caseId; see OperatorConsolePointer.tsx.
 *
 * Role compares lowercase both sides (`.claude/rules/authz-case-insensitive.md`).
 *
 * No top-level view-model interfaces; useState shapes are anonymous, and
 * only direct schema aliases (`type X = components['schemas']['Y']`) are
 * declared as named types.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Heading,
  BodyText,
  Button,
  Badge,
  Card,
  Textarea,
  Alert,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/client';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';
import { tenantsApi } from '../../api/services/tenants';
import { SupportOperatorConsolePointer } from './OperatorConsolePointer';
import { SupportServiceMissingError, SupportServiceNotice, supportFailure } from './SupportServiceNotice';

type SupportCase = components['schemas']['SupportCase'];
type SupportCaseMessage = components['schemas']['SupportCaseMessage'];
type SupportCaseStatus = components['schemas']['SupportCaseStatus'];
type SupportCasePriority = components['schemas']['SupportCasePriority'];

const FI_OPERATOR_ROLES = [
  'fi_admin',
  'fi_loan_officer',
  'fi_compliance',
] as const;

function statusVariant(status: SupportCaseStatus): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'open':
      return 'warning';
    case 'awaiting_user':
    case 'awaiting_fi_input':
      return 'info';
    case 'awaiting_platform_admin':
      return 'warning';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function priorityVariant(priority: SupportCasePriority): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (priority) {
    case 'P1':
      return 'error';
    case 'P2':
      return 'warning';
    case 'P3':
      return 'info';
    case 'P4':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function SupportCaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = hasPlatformAdminRole(user?.roles);
  // The support service refuses platform-admin callers on /fi-cases, so a
  // platform-admin session is never served an FI case, FI role or not.
  const isFiOperator = !isPlatformAdmin && FI_OPERATOR_ROLES.some((r) => roles.includes(r));

  const [supportCase, setSupportCase] = useState<SupportCase | null>(null);
  const [messages, setMessages] = useState<SupportCaseMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);

  // Compose state
  const [replyBody, setReplyBody] = useState<string>('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replyPosting, setReplyPosting] = useState<boolean>(false);

  const loadDetail = useCallback(async () => {
    if (!caseId) return;
    if (!isFiOperator) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      const { data, error, response } = await supportPlatformClient.GET(
        '/api/v1/support/fi-cases/{caseId}',
        { params: { path: { caseId } } },
      );
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load case');
      setSupportCase(data ?? null);

      const msg = await supportPlatformClient.GET(
        '/api/v1/support/fi-cases/{caseId}/messages',
        { params: { path: { caseId } } },
      );
      if (msg.error !== undefined || !msg.response.ok) throw supportFailure(msg.response, msg.error, 'Failed to load messages');
      setMessages(msg.data?.messages ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load case');
      setSupportCase(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, isFiOperator]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  // The case's institution by name, never its id (GH #678 ruling 2026-10-23:
  // the Support FI view shows institution names). GET /tenants/{tenantId}
  // (platform-admin.openapi.yaml, TenantDetail.name) answers an FI operator
  // for its own tenant only; a refusal, a failure or a pending read leave the
  // row out rather than show the id.
  const caseTenantId = supportCase?.tenantId;
  const [institutionName, setInstitutionName] = useState<string | null>(null);
  useEffect(() => {
    setInstitutionName(null);
    if (!caseTenantId) return;
    let canceled = false;
    tenantsApi
      .get(caseTenantId)
      .then((tenant) => {
        const name = tenant.name.trim();
        if (!canceled && name) setInstitutionName(name);
      })
      .catch(() => {
        // No name: the row stays out.
      });
    return () => {
      canceled = true;
    };
  }, [caseTenantId]);

  const handleReply = useCallback(async () => {
    if (!caseId) return;
    if (!replyBody.trim()) {
      setReplyError('Message body is required.');
      return;
    }
    setReplyPosting(true);
    setReplyError(null);
    try {
      if (!isFiOperator) {
        throw new Error('Not authorized to reply on this case.');
      }
      const { error, response } = await supportPlatformClient.POST(
        '/api/v1/support/fi-cases/{caseId}/messages',
        {
          params: { path: { caseId } },
          body: { body: replyBody },
        },
      );
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to post message');
      setReplyBody('');
      await loadDetail();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setReplyError(err instanceof Error ? err.message : 'Failed to post message');
    } finally {
      setReplyPosting(false);
    }
  }, [caseId, replyBody, isFiOperator, loadDetail]);

  if (isPlatformAdmin) {
    return <SupportOperatorConsolePointer />;
  }

  if (!isFiOperator) {
    return (
      <div className="p-6 space-y-4">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <Card>
          <div className="p-4">
            <Alert type="error">
              You do not have access to this case. Support cases are visible to Aaim
              support and, for your institution’s own cases, to staff with the FI Admin,
              FI Loan Officer or FI Compliance role.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <BodyText color="secondary">Loading case...</BodyText>
      </div>
    );
  }

  if (serviceMissing) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/admin/support" className="text-primary hover:underline text-sm">
          &larr; Back to cases
        </Link>
        <SupportServiceNotice />
      </div>
    );
  }

  if (loadError || !supportCase) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/admin/support" className="text-primary hover:underline text-sm">
          &larr; Back to cases
        </Link>
        <Alert type="error">{loadError ?? 'Case not found.'}</Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin/support" className="text-primary hover:underline text-sm">
        &larr; Back to cases
      </Link>

      <div className="space-y-1">
        {/* Page title lives in the sticky topnav (AppHeader); the status badges
            stay in the body, no redundant H1. */}
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant(supportCase.status)} size="sm">
            {supportCase.status}
          </Badge>
          <Badge variant={priorityVariant(supportCase.priority)} size="sm">
            {supportCase.priority}
          </Badge>
          <BodyText size="sm" color="secondary">
            Opened {new Date(supportCase.openedAt).toLocaleString()}
          </BodyText>
        </div>
      </div>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Case</Heading>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-tertiary">Category</dt>
              <dd className="text-primary">{supportCase.category}</dd>
            </div>
            <div>
              <dt className="text-tertiary">Channel</dt>
              <dd className="text-primary">{supportCase.sourceChannel}</dd>
            </div>
            {institutionName && (
              <div>
                <dt className="text-tertiary">Institution</dt>
                <dd className="text-primary">{institutionName}</dd>
              </div>
            )}
            {/* No Consumer or Assigned agent row (GH #4245, ruling 2026-10-23
                21:47Z): no endpoint an FI session can call names either id, so
                the FI view showed raw UUIDs. platform.aaim.com /support/:caseId
                keeps both. */}
            {supportCase.slaTargetAt && (
              <div>
                <dt className="text-tertiary">SLA target</dt>
                <dd className="text-primary">
                  {new Date(supportCase.slaTargetAt).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </Card>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Messages</Heading>
          {messages.length === 0 ? (
            <BodyText size="sm" color="secondary">
              No messages on this case yet.
            </BodyText>
          ) : (
            <ul className="space-y-3">
              {messages.map((m) => (
                <li key={m.id} className="border-b border-recessed pb-3 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="neutral" size="sm">
                      {m.authorType}
                    </Badge>
                    <span className="text-xs text-tertiary">
                      {new Date(m.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-primary whitespace-pre-wrap">{m.body}</div>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-3 border-t border-recessed space-y-2">
            {replyError && <Alert type="error">{replyError}</Alert>}
            <Textarea
              label="Reply"
              value={replyBody}
              onChange={(v) => setReplyBody(v)}
              rows={4}
              required
              placeholder="Write your reply..."
            />
            <Button variant="primary" onClick={handleReply} disabled={replyPosting}>
              {replyPosting ? 'Sending...' : 'Send reply'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
